import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query'
import {api} from '@/lib/api'
import {base, type IngestaJob} from '@/hooks/bateria/use-bateria'
import type {Page} from '@/hooks/empresa/organizacion/use-businesses'
import type {components} from '@/lib/api-schema'

export type BateriaBitacoraEntrada = components['schemas']['BateriaBitacoraEntradaRead']
export type BateriaBitacoraTipo = components['schemas']['BateriaBitacoraTipo']

export const BITACORA_PAGE_SIZE = 20

export type BitacoraFilters = {
  page: number
  tipo: BateriaBitacoraTipo | ''
  search: string
}

/** G13: página y filtro viajan al servidor; nunca se recorta una lista ya traída. */
export function useBitacora(
  businessId: string | undefined,
  evaluacionId: string | undefined,
  enabled: boolean,
  filters: BitacoraFilters
) {
  return useQuery({
    queryKey: ['bateria-bitacora', businessId, evaluacionId, filters.page, filters.tipo, filters.search],
    queryFn: async () =>
      (
        await api.get<Page<BateriaBitacoraEntrada>>(`${base(businessId!)}/evaluaciones/${evaluacionId}/bitacora`, {
          params: {
            page: filters.page,
            page_size: BITACORA_PAGE_SIZE,
            ...(filters.tipo ? {tipo: filters.tipo} : {}),
            ...(filters.search ? {search: filters.search} : {}),
          },
        })
      ).data,
    enabled: enabled && !!businessId && !!evaluacionId,
  })
}

/** Las páginas que no llegaron cuentan como perdidas; el descarte mueve la fase de la evaluación y escribe
 * en la bitácora. 409 si la tanda ya no está detenida. */
export function useDescartarTanda(businessId: string, evaluacionId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (jobId: string) =>
      (await api.post<IngestaJob>(`${base(businessId)}/evaluaciones/${evaluacionId}/ingestas/${jobId}/descartar`)).data,
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ['bateria-ingestas', businessId, evaluacionId]})
      queryClient.invalidateQueries({queryKey: ['bateria-evaluacion', businessId, evaluacionId]})
      queryClient.invalidateQueries({queryKey: ['bateria-bitacora', businessId, evaluacionId]})
    },
  })
}
